import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, CalendarPlus, CheckCircle2, ChevronDown, ChevronUp, Circle, CircleDot, Link2, RefreshCw, SkipForward, XCircle } from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { caixaAlta, cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Campo } from '@/componentes/ui/primitivos';
import { LogoSlack } from './MeuSlack';
import { haQuanto, type DetalheNota, type Divergencia, precosDaMensagem, type Status, textoProgresso } from './tipos';

/**
 * O que falta para a nota seguir, em sequência, com de quem é a vez (pedido do
 * Paulo, 30/10/2026: os detalhes soltos pela gaveta confundiam). Primeiro a
 * conferência com a OC, passo a passo — OC, produto, preço, quantidade,
 * pré-pedido —, depois as fases da esteira: entrada fiscal, agendamento,
 * recebimento. Item achado pelo EAN do XML não é pendência: o passo diz
 * "associado pelo EAN" e o de-para fica como opção.
 */

type Estado = 'feito' | 'pendente' | 'depois';

/** Número em pt-BR ("1.234,56", "R$ 567") para conta. */
const real = (s: string | null | undefined): number => {
  const n = Number((s ?? '').replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const brl = (v: number): string => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

/**
 * O valor em jogo nas divergências de um passo: preço (o "São R$ X a mais" da
 * mensagem), quantidade (o que passou do saldo × preço da nota) e item fora do
 * pré-pedido (o item inteiro). null = não dá para calcular.
 */
function impacto(lista: Divergencia[], itens: DetalheNota['itens']): number | null {
  let total = 0;
  let algum = false;
  for (const x of lista) {
    const item = itens.find((i) => i.nItem === x.nItem);
    if (x.regra === 'preco') {
      const m = /São R\$\s*([\d.,]+) a mais/.exec(x.mensagem);
      if (m) {
        total += real(m[1]);
        algum = true;
      }
    } else if (x.regra === 'quantidade' && item?.valorUnit != null) {
      const a = real(x.veio) - real((x.esperado ?? '').replace(/^até\s*/, ''));
      if (a > 0) {
        total += a * Number(item.valorUnit);
        algum = true;
      }
    } else if (x.regra === 'fora_pre_pedido' && item?.valorTotal != null) {
      total += Number(item.valorTotal);
      algum = true;
    }
  }
  return algum ? total : null;
}

interface Passo {
  titulo: string;
  detalhe: string;
  estado: Estado;
  dono: string | null;
  /** Ação curta dentro do passo (ex.: gravar o de-para). */
  extra?: React.ReactNode;
  /** Outra saída, em letra menor (ex.: decidir pelo Slack). */
  dica?: string;
  /** O passo depende de aprovação: mostra Aceitar, Recusar e "Pedir autorização no Slack". */
  aprovacao?: boolean;
  /** Os tipos de divergência que o "Aceitar" deste passo fecha (sem lista: todas). */
  regras?: string[];
  /** A ação principal do passo, quando ele é o atual (o botão que resolve, aqui mesmo). */
  acao?: React.ReactNode;
  /** Passou sem acontecer no WMS (ex.: a cega saiu direto no ERP): ↷ em vez de ✓. */
  fora?: boolean;
  /** Valor em jogo nas divergências do passo. */
  impacto?: number | null;
  /** "Chamar no Slack": quem já vem marcado (vazio = a pessoa escolhe). */
  chamar?: string[];
  /** O texto do botão de chamar, quando o motivo é específico (ex.: "Perguntar a OC certa a Joaquim"). */
  chamarRotulo?: string;
  /** As divergências do passo: a mensagem do "Chamar" é escrita com elas. */
  divs?: Divergencia[];
}

/** Ação pequena dentro do passo: com borda e ícone, para não parecer texto. */
const TOM_BOTAO = {
  marca: 'border-primaria/40 text-primaria hover:border-primaria hover:bg-primaria-suave/50',
  ok: 'border-status-livre/50 text-status-livre hover:border-status-livre hover:bg-status-livreFundo/60',
  perigo: 'border-status-divergencia/40 text-status-divergencia hover:border-status-divergencia hover:bg-status-divergenciaFundo/60',
} as const;

function BotaoPasso({
  icone: Icone,
  children,
  onClick,
  desabilitado,
  tom = 'marca',
}: {
  icone: React.ElementType;
  children: React.ReactNode;
  onClick: () => void;
  desabilitado?: boolean;
  tom?: keyof typeof TOM_BOTAO;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={desabilitado}
      className={cn('inline-flex items-center gap-1 rounded-md border bg-superficie px-2 py-0.5 text-2xs font-semibold transition disabled:opacity-60', TOM_BOTAO[tom])}
    >
      <Icone className="h-3 w-3" />
      {children}
    </button>
  );
}

/**
 * O passo que depende de decisão: aceitar (com motivo), recusar (com motivo,
 * só enquanto a nota está na conferência) ou pedir autorização no Slack, em
 * nome da pessoa. São atalhos para as MESMAS ações dos cartões de "O que fazer
 * agora" (mesmas rotas, mesmo motivo obrigatório, mesma linha do tempo).
 */
function DecisaoDoPasso({ d, regras, chamar }: { d: DetalheNota; regras?: string[] | undefined; chamar?: React.ReactNode }): JSX.Element {
  const n = d.nota;
  const { temPermissao } = useSessao();
  const cliente = useQueryClient();
  const [decidindo, definirDecidindo] = React.useState<'aceitar' | 'recusar' | null>(null);
  const [motivo, definirMotivo] = React.useState('');
  const eu = useQuery<{ conectado: boolean; comoPessoa: boolean; disponivel: boolean }>({ queryKey: ['slack-eu'], queryFn: () => api.get('/api/slack/eu') });
  const depois = (): void => {
    void cliente.invalidateQueries({ queryKey: ['entrada', n.chave] });
    void cliente.invalidateQueries({ queryKey: ['entradas'] });
  };
  // Comprador da OC + quem aprova DOH, em nome da pessoa (token dela); a thread fica na nota (aceitar/recusar nela agem aqui).
  const pedir = useMutation({
    mutationFn: () => api.post<{ link: string | null }>(`/api/entradas/nota/${n.chave}/pedir-autorizacao`, {}),
    onSuccess: depois,
  });
  const decidir = useMutation({
    // Aceitar o passo fecha só as divergências dele (aceitar o preço não fecha a quantidade).
    mutationFn: (caminho: 'aceitar-divergencia' | 'recusar') =>
      api.post(`/api/entradas/nota/${n.chave}/${caminho}`, { motivo: motivo.trim(), ...(caminho === 'aceitar-divergencia' && regras ? { regras } : {}) }),
    onSuccess: () => {
      definirDecidindo(null);
      definirMotivo('');
      depois();
    },
  });
  const podeAceitar = temPermissao('entradas.comprador') || temPermissao('entradas.fiscal');
  // Recusar só enquanto a nota está na conferência: depois de lançada no ERP é aceitar ou tratar com o fornecedor.
  const podeRecusar = temPermissao('entradas.comprador') && (n.status === 'divergente' || n.status === 'sem_oc');
  const erro = (pedir.error ?? decidir.error) as unknown;
  return (
    <div className="mt-1.5 space-y-1.5 text-2xs text-textoSuave">
      {decidindo ? (
        <div className={cn('space-y-1.5 rounded-md border bg-superficie p-2', decidindo === 'aceitar' ? 'border-status-livre/40' : 'border-status-divergencia/40')}>
          <label className="block space-y-1">
            <span className="font-semibold text-texto">{decidindo === 'aceitar' ? 'Motivo do aceite' : 'Motivo da recusa'}</span>
            <Campo
              autoFocus
              className="h-8 text-xs"
              placeholder={decidindo === 'aceitar' ? 'Ex.: reajuste combinado com o fornecedor' : 'Ex.: preço acima do combinado'}
              value={motivo}
              onChange={(e) => definirMotivo(e.target.value)}
            />
          </label>
          <div className="flex justify-end gap-1.5">
            <Botao variante="fantasma" tamanho="micro" onClick={() => definirDecidindo(null)}>
              Voltar
            </Botao>
            <Botao
              variante={decidindo === 'aceitar' ? 'primaria' : 'perigo'}
              tamanho="micro"
              disabled={!motivo.trim()}
              carregando={decidir.isPending}
              onClick={() => decidir.mutate(decidindo === 'aceitar' ? 'aceitar-divergencia' : 'recusar')}
            >
              {decidindo === 'aceitar' ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
              {decidindo === 'aceitar' ? 'Confirmar aceite' : 'Confirmar recusa'}
            </Botao>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {podeAceitar ? (
            <BotaoPasso icone={CheckCircle2} tom="ok" onClick={() => definirDecidindo('aceitar')}>
              Aceitar
            </BotaoPasso>
          ) : null}
          {podeRecusar ? (
            <BotaoPasso icone={XCircle} tom="perigo" onClick={() => definirDecidindo('recusar')}>
              Recusar
            </BotaoPasso>
          ) : null}
          {/* Pedido sai como a pessoa: sem o Slack ligado, o botão é o de conectar. */}
          {eu.data && !eu.data.comoPessoa && eu.data.disponivel ? (
            <a
              href="/api/slack/conectar"
              title="Para o pedido sair em seu nome, ligue seu Slack ao WMS (uma vez só)."
              className="inline-flex items-center gap-1 rounded-md border border-primaria/40 bg-superficie px-2 py-0.5 font-semibold text-primaria transition hover:border-primaria hover:bg-primaria-suave/50"
            >
              <LogoSlack className="h-3 w-3" /> Conectar com o Slack para pedir autorização
            </a>
          ) : (
            <BotaoPasso icone={LogoSlack} onClick={() => pedir.mutate()} desabilitado={pedir.isPending}>
              {pedir.isPending ? 'Pedindo…' : d.autorizacaoPedida ? 'Pedir de novo' : 'Pedir autorização no Slack'}
            </BotaoPasso>
          )}
          {chamar}
          {d.autorizacaoPedida ? (
            <span>
              pedida {haQuanto(d.autorizacaoPedida.quando)}
              {d.autorizacaoPedida.autor ? ` por ${d.autorizacaoPedida.autor}` : ''}
            </span>
          ) : null}
        </div>
      )}
      {erro ? <p className="text-status-divergencia">{erro instanceof ErroHttp ? erro.message : String(erro)}</p> : null}
    </div>
  );
}

/**
 * A conferência de uma nota já conferida, item a item: SKUs reconhecidos, preço,
 * quantidade e pré-pedido — verde quando bateu (ou foi aceito), vermelho quando
 * ainda está aberto.
 */
function SelosConferencia({ d }: { d: DetalheNota }): JSX.Element {
  const aberta = (regras: string[]): boolean => d.divergencias.some((x) => regras.includes(x.regra));
  const comSku = d.itens.filter((i) => i.produto != null).length;
  const selos: [string, boolean][] = [
    [`SKUs ${comSku} de ${d.itens.length}`, comSku === d.itens.length && !aberta(['produto', 'item_fora_oc'])],
    ['Preço', !aberta(['preco'])],
    ['Quantidade', !aberta(['quantidade'])],
    ['Pré-pedido', !aberta(['fora_pre_pedido'])],
  ];
  return (
    <span className="flex flex-wrap gap-1">
      {selos.map(([rotulo, ok]) => (
        <span
          key={rotulo}
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-2xs font-semibold',
            ok ? 'bg-status-livreFundo text-status-livre' : 'bg-status-divergenciaFundo text-status-divergencia',
          )}
        >
          {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
          {rotulo}
        </span>
      ))}
    </span>
  );
}

/**
 * Sem OC: "Perguntar a OC ao comprador no Slack", como a pessoa, com os itens,
 * as OCs abertas do fornecedor e a DANFE na thread. Sem o Slack ligado, o botão
 * é o de conectar (o pedido só sai em nome de quem pede).
 */
/**
 * "Validar no ERP": o que o fiscal fazia salvando a NF de compra no Procfit
 * (título a pagar + estoque no centro de saída), pelo WMS. A prévia mostra o
 * que vai ser gravado; confirmar é do fiscal.
 */
interface PreviaValidacao {
  nfCompra: number;
  centro: number | null;
  titulos: { titulo: string; vencimento: string; valor: number }[];
  itens: { produto: number; descricao: string | null; quantidade: number; centroHoje: number }[];
  problemas: string[];
}
const reais = (v: number): string => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Validar no ERP (tira o "ignorar validação"): prévia e confirmação. Também na edição da NF de compra. */
export function ValidarCompra({ chave, rotulo }: { chave: string; rotulo?: string }): JSX.Element {
  const { temPermissao } = useSessao();
  const cliente = useQueryClient();
  const [aberto, definirAberto] = React.useState(false);
  const previa = useQuery<PreviaValidacao>({
    queryKey: ['entrada-validar-compra', chave],
    queryFn: () => api.get(`/api/entradas/nota/${chave}/validar-compra`),
    enabled: aberto,
  });
  const validar = useMutation({
    mutationFn: () => api.post<{ titulos: number; estoque: number; centro: number }>(`/api/entradas/nota/${chave}/validar-compra`, {}),
    onSuccess: () => {
      definirAberto(false);
      void cliente.invalidateQueries({ queryKey: ['entrada', chave] });
      void cliente.invalidateQueries({ queryKey: ['entradas'] });
      // Na edição da NF de compra: o cabeçalho passa a "Validada".
      void cliente.invalidateQueries({ queryKey: ['nf-compra'] });
      void cliente.invalidateQueries({ queryKey: ['nf-compra-lista'] });
    },
  });
  const p = previa.data;
  return (
    <div className="space-y-1.5 text-2xs">
      <BotaoPasso icone={CheckCircle2} tom="ok" onClick={() => definirAberto((x) => !x)}>
        {aberto ? 'Fechar' : rotulo ?? 'Validar no ERP (título e estoque)'}
      </BotaoPasso>
      {aberto ? (
        <div className="space-y-2 rounded-lg border border-borda bg-superficie p-2.5">
          {previa.isLoading ? <p className="text-textoSuave">Conferindo…</p> : null}
          {previa.error ? <p className="text-status-divergencia">{previa.error instanceof ErroHttp ? previa.error.message : String(previa.error)}</p> : null}
          {p ? (
            <>
              <p className="text-textoFraco">
                NF de compra <b className="text-texto">{p.nfCompra}</b>: o WMS salva como o Procfit — itens no centro{' '}
                <b className="text-texto">{p.centro ?? '?'}</b>, tira o "ignorar validação" e gera:
              </p>
              <div>
                <p className="font-semibold text-texto">Títulos a pagar</p>
                {p.titulos.length ? (
                  <ul className="tabular">
                    {p.titulos.map((t) => (
                      <li key={t.titulo}>
                        {t.titulo} · vence {t.vencimento.split('-').reverse().join('/')} · {reais(t.valor)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-status-divergencia">nenhuma parcela</p>
                )}
              </div>
              <div>
                <p className="font-semibold text-texto">Estoque (entrada no {p.centro ?? '?'})</p>
                <ul className="tabular">
                  {p.itens.map((i) => (
                    <li key={i.produto}>
                      {i.produto} {i.descricao ?? ''} · {i.quantidade} un
                      {i.centroHoje !== p.centro ? <span className="text-textoSuave"> (hoje no {i.centroHoje})</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
              {p.problemas.length ? (
                <ul className="text-status-divergencia">
                  {p.problemas.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              ) : temPermissao('entradas.fiscal') ? (
                <Botao tamanho="pequeno" variante="primaria" carregando={validar.isPending} onClick={() => validar.mutate()}>
                  <CheckCircle2 className="h-4 w-4" /> Confirmar: gerar títulos e estoque
                </Botao>
              ) : (
                <p className="text-textoSuave">Confirmar é do fiscal (entradas.fiscal).</p>
              )}
              {validar.error ? (
                <p className="text-status-divergencia">{validar.error instanceof ErroHttp ? validar.error.message : String(validar.error)}</p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function PerguntarOc({ d }: { d: DetalheNota }): JSX.Element {
  const cliente = useQueryClient();
  const eu = useQuery<{ comoPessoa: boolean; disponivel: boolean }>({ queryKey: ['slack-eu'], queryFn: () => api.get('/api/slack/eu') });
  const perguntar = useMutation({
    mutationFn: () => api.post<{ link: string | null; danfe: boolean }>(`/api/entradas/nota/${d.nota.chave}/perguntar-oc`, {}),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['entrada', d.nota.chave] }),
  });
  if (eu.data && !eu.data.comoPessoa && eu.data.disponivel) {
    return (
      <a
        href="/api/slack/conectar"
        title="Para a pergunta sair em seu nome, ligue seu Slack ao WMS (uma vez só)."
        className="inline-flex items-center gap-1 rounded-md border border-primaria/40 bg-superficie px-2 py-0.5 text-2xs font-semibold text-primaria transition hover:border-primaria hover:bg-primaria-suave/50"
      >
        <LogoSlack className="h-3 w-3" /> Conectar com o Slack para perguntar a OC
      </a>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-2xs text-textoSuave">
      <BotaoPasso icone={LogoSlack} onClick={() => perguntar.mutate()} desabilitado={perguntar.isPending}>
        {perguntar.isPending ? 'Perguntando…' : d.ocPerguntada ? 'Perguntar de novo' : 'Perguntar a OC ao comprador no Slack'}
      </BotaoPasso>
      {d.ocPerguntada ? (
        <span>
          perguntada {haQuanto(d.ocPerguntada.quando)}
          {d.ocPerguntada.autor ? ` por ${d.ocPerguntada.autor}` : ''} · resposta "oc 12345" na thread confere sozinha
        </span>
      ) : (
        <span>vai com os itens, as OCs abertas do fornecedor e a DANFE</span>
      )}
      {perguntar.data && !perguntar.data.danfe ? <span className="text-status-atencao">a DANFE não foi anexada (veja a linha do tempo)</span> : null}
      {perguntar.error ? (
        <span className="text-status-divergencia">{perguntar.error instanceof ErroHttp ? perguntar.error.message : String(perguntar.error)}</span>
      ) : null}
    </span>
  );
}

/**
 * Os itens da nota lado a lado com o SKU do ERP: código e nome na nota → SKU e
 * nome no ERP, e como foi achado (EAN sem de-para, ou de-para). Item sem SKU
 * fica vermelho com "Associar", que abre a comparação para dizer qual é.
 */
function ItensDoProduto({
  d,
  aoAbrir,
  aberto,
}: {
  d: DetalheNota;
  aoAbrir: (modo: 'pendencia' | 'cadastro') => void;
  aberto: 'pendencia' | 'cadastro' | null;
}): JSX.Element {
  const semDePara = new Set((d.deParas ?? []).map((p) => p.nItem));
  const curto = (t: string | null | undefined, n: number): string => (t ?? '').replace(/^\(\)\s*/, '').replace(/\s+(LOTE|EAN)[:\s].*$/i, '').slice(0, n);
  const pelaEan = d.itens.filter((i) => i.produto != null && semDePara.has(i.nItem));
  return (
    <div className="mt-0.5 space-y-1">
      <ul className="divide-y divide-borda overflow-hidden rounded-md border border-borda text-2xs">
        {d.itens.slice(0, 8).map((i) => (
          <li key={i.nItem} className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-2 py-1">
            <span className="min-w-0" title={i.descricao ?? undefined}>
              <span className="block truncate">
                <b className="font-mono text-texto">{i.cprod ?? '—'}</b> <span className="text-textoFraco">{curto(i.descricao, 34)}</span>
              </span>
              {/* O EAN dos dois lados, um embaixo do outro: dá para ver que batem. */}
              <span className="block font-mono text-[10px] text-textoSuave">EAN {i.ean?.trim() || '—'}</span>
            </span>
            <span className="text-textoSuave">→</span>
            {i.produto != null ? (
              <span className="flex min-w-0 items-center gap-1.5" title={i.descricaoErp ?? undefined}>
                <span className="min-w-0">
                  <span className="block truncate">
                    <b className="tabular-nums text-texto">SKU {i.produto}</b> <span className="text-textoFraco">{curto(i.descricaoErp, 34)}</span>
                  </span>
                  <span className="flex items-center gap-1 font-mono text-[10px] text-textoSuave" title={i.eansErp?.length ? `EANs no PBS: ${i.eansErp.join(', ')}` : 'Sem EAN no cadastro do PBS'}>
                    EAN {i.eanErp ?? '—'}
                    {i.eanErp && i.ean && i.eanErp === i.ean.trim() ? (
                      <CheckCircle2 className="h-2.5 w-2.5 text-status-livre" aria-label="EAN igual ao da nota" />
                    ) : null}
                    {(i.eansErp?.length ?? 0) > 1 ? <span>+{(i.eansErp?.length ?? 1) - 1} no PBS</span> : null}
                  </span>
                </span>
                {semDePara.has(i.nItem) ? (
                  <span className="shrink-0 rounded bg-primaria-suave px-1 font-semibold text-primaria" title={`Achado pelo EAN ${i.ean ?? ''} do XML; o código do fornecedor ainda não tem de-para no ERP.`}>
                    EAN
                  </span>
                ) : (
                  <span className="shrink-0 rounded bg-status-livreFundo px-1 font-semibold text-status-livre" title="O código do fornecedor tem de-para no ERP.">
                    de-para
                  </span>
                )}
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <span className="rounded bg-status-divergenciaFundo px-1 font-semibold text-status-divergencia">sem SKU</span>
                {d.fornecedorNoErp === false ? null : d.podeDePara ? (
                  <BotaoPasso icone={Link2} tom="perigo" onClick={() => aoAbrir('pendencia')}>
                    {aberto === 'pendencia' ? 'Fechar' : 'Associar'}
                  </BotaoPasso>
                ) : null}
              </span>
            )}
          </li>
        ))}
      </ul>
      {d.itens.length > 8 ? <p className="text-2xs text-textoSuave">e mais {d.itens.length - 8} item(ns)</p> : null}
      {pelaEan.length && d.podeDePara ? (
        <BotaoPasso icone={Link2} onClick={() => aoAbrir('cadastro')}>
          {aberto === 'cadastro' ? 'Fechar' : `Gravar o de-para no cadastro (${pelaEan.length})`}
        </BotaoPasso>
      ) : null}
    </div>
  );
}

/** O que a cega contou diferente da nota, produto a produto. */
function DiferencasCega({ c }: { c: NonNullable<DetalheNota['contagemCega']> }): JSX.Element {
  const un = (v: number): string => v.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
  return (
    <div className="mt-0.5 rounded-md border border-status-divergencia/40 bg-status-divergenciaFundo/40 px-2 py-1.5">
      <p className="flex items-center gap-1 text-2xs font-semibold text-status-divergencia">
        <XCircle className="h-3 w-3" /> Contagem da cega {c.recebimento} diferente da nota
      </p>
      <ul className="mt-0.5 space-y-0.5 text-2xs text-texto">
        {c.itens.map((i) => (
          <li key={i.produto}>
            <b className="tabular-nums">{i.produto}</b> <span className="text-textoFraco">{(i.descricao ?? '').replace(/^\(\)\s*/, '').slice(0, 42)}</span>
            {' · '}nota <b className="tabular-nums">{un(i.nota)}</b> · contado <b className="tabular-nums">{un(i.contado)}</b>{' '}
            <b className={cn('tabular-nums', i.contado < i.nota ? 'text-status-divergencia' : 'text-status-atencao')}>
              ({i.contado - i.nota > 0 ? '+' : ''}
              {un(i.contado - i.nota)})
            </b>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Indicar a OC no próprio passo: a OC aberta do fornecedor que cobre todos os
 * itens vira botão; senão, o número digitado. A mesma ação do "É esta" da lista.
 */
function UsarOc({ d }: { d: DetalheNota }): JSX.Element | null {
  const { temPermissao } = useSessao();
  const cliente = useQueryClient();
  const [oc, definirOc] = React.useState('');
  const usar = useMutation({
    mutationFn: (numero: number) => api.post(`/api/entradas/nota/${d.nota.chave}/oc`, { oc: numero }),
    onSuccess: () => {
      definirOc('');
      void cliente.invalidateQueries({ queryKey: ['entrada', d.nota.chave] });
      void cliente.invalidateQueries({ queryKey: ['entradas'] });
    },
  });
  if (!temPermissao('entradas.comprador')) return null;
  const cobre = d.ocsDoFornecedor.filter((o) => o.itensNota > 0 && o.emComum === o.itensNota && o.oc !== d.nota.pedidoCompra).slice(0, 2);
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-2xs">
      {cobre.map((o) => (
        <BotaoPasso key={o.oc} icone={CheckCircle2} tom="ok" onClick={() => usar.mutate(o.oc)} desabilitado={usar.isPending}>
          Usar OC {o.oc} (cobre {o.emComum} de {o.itensNota})
        </BotaoPasso>
      ))}
      <Campo
        inputMode="numeric"
        className="h-7 w-32 text-xs"
        placeholder={cobre.length ? 'Outra OC: nº' : 'Nº da OC'}
        value={oc}
        onChange={(e) => definirOc(e.target.value.replace(/\D/g, ''))}
      />
      {oc ? (
        <BotaoPasso icone={CheckCircle2} onClick={() => usar.mutate(Number(oc))} desabilitado={usar.isPending}>
          {usar.isPending ? 'Conferindo…' : `Usar OC ${oc}`}
        </BotaoPasso>
      ) : null}
      {usar.error ? <span className="text-status-divergencia">{usar.error instanceof ErroHttp ? usar.error.message : String(usar.error)}</span> : null}
    </div>
  );
}

const brlUn = (v: number): string => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qtdTxt = (v: number): string => v.toLocaleString('pt-BR', { maximumFractionDigits: 3 });

/** Uma divergência como frase para o comprador: o produto, os números e a diferença. */
function fraseDivergencia(x: Divergencia, d: DetalheNota): string {
  const item = d.itens.find((i) => i.nItem === x.nItem);
  const nomeItem = (item?.descricaoErp ?? item?.descricao ?? '').replace(/^\(\)\s*/, '').replace(/\s+(LOTE|EAN)[:\s].*$/i, '').slice(0, 45);
  const produto = `*${nomeItem || 'item'}*${item?.produto ? ` (SKU ${item.produto})` : ''}`;
  const oc = d.nota.pedidoCompra;
  if (x.regra === 'quantidade') {
    const veio = real(x.veio);
    const saldo = real((x.esperado ?? '').replace(/^até\s*/, ''));
    const sobra = veio - saldo;
    const valor = item?.valorUnit != null ? ` (${brl(sobra * Number(item.valorUnit))})` : '';
    // O contexto da OC, que a mensagem da divergência traz entre parênteses: pré-pedido, OC alterada, já recebido.
    const ctx = /\((pré-pedido .*já recebido [\d.,]+)\)/.exec(x.mensagem)?.[1];
    return `veio com *${qtdTxt(veio)} un* do ${produto}, mas o saldo da OC ${oc ?? ''} é *${qtdTxt(saldo)} un*: são *${qtdTxt(sobra)} un a mais*${valor}.` +
      (ctx ? `\nObs.: ${ctx.replace(/ · /g, '; ')}.` : '');
  }
  if (x.regra === 'preco') {
    const pr = precosDaMensagem(x.mensagem);
    const a = /São R\$\s*([\d.,]+) a mais(?: em ([\d.,]+) un)?/.exec(x.mensagem);
    if (pr) {
      return `o ${produto} veio a *${brlUn(pr.nota)}/un*, o aprovado é *${brlUn(pr.ref)}/un*${pr.prePedido ? ` (pré-pedido ${pr.prePedido})` : ''}` +
        (a ? `: *${brl(real(a[1]))} a mais*${a[2] ? ` em ${a[2]} un` : ''}.` : '.');
    }
  }
  if (x.regra === 'fora_pre_pedido') {
    const pre = /pré-pedido (\d+)/.exec(x.mensagem)?.[1];
    return `o ${produto} não estava no pré-pedido${pre ? ` ${pre}` : ''} e entrou na OC ${oc ?? ''} depois. Foi aprovado?`;
  }
  if (x.regra === 'item_fora_oc') {
    return `o ${produto} não está na OC ${oc ?? ''}${x.ocSugerida ? `, mas está na OC ${x.ocSugerida}` : ''}.`;
  }
  return x.mensagem.replace(/^Item \d+ · /, '').replace(/\s*\(toler[^)]*\)/, '');
}

/**
 * A mensagem do "Chamar … no Slack": curta, escrita para quem vai ler (não o
 * texto da tela), com os números e a pergunta no fim. Abre na Conversa da
 * gaveta para revisar antes de enviar; sai em nome da pessoa, com a DANFE.
 */
function mensagemDoPasso(d: DetalheNota, p: Passo): string {
  const n = d.nota;
  const forn = (n.emitenteNome ?? '').split(' ').slice(0, 2).join(' ').toLocaleUpperCase('pt-BR');
  const nf = `a NF ${n.numero ?? ''}${forn ? ` da *${forn}*` : ''}`;
  const divs = (p.divs ?? []).slice(0, 3);
  if (!divs.length) return `Sobre ${nf}: ${p.detalhe} Pode verificar?`;
  const frases = divs.map((x) => fraseDivergencia(x, d));
  const corpo = frases.length === 1 ? `${nf} ${frases[0]}` : `${nf}:\n${frases.map((f) => `• ${f}`).join('\n')}`;
  const mais = (p.divs?.length ?? 0) > divs.length ? `\n• e mais ${(p.divs?.length ?? 0) - divs.length} divergência(s)` : '';
  const ocErrada = divs.some((x) => ['item_fora_oc', 'oc', 'fornecedor', 'destinatario', 'oc_fechada'].includes(x.regra));
  const pergunta = ocErrada ? 'Qual OC vale para esta nota? Pode responder aqui: `oc 12345`.' : 'Aceitamos, pedimos correção ou devolvemos?';
  return `${corpo.charAt(0).toUpperCase()}${corpo.slice(1)}${mais}\n${pergunta}`;
}

/** Confere a nota de novo (a mesma ação do Revalidar), para trocar uma conferência antiga pela atual. */
function ConferirDeNovo({ chave }: { chave: string }): JSX.Element {
  const cliente = useQueryClient();
  const conferir = useMutation({
    mutationFn: () => api.post(`/api/entradas/nota/${chave}/revalidar`, {}),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['entrada', chave] });
      void cliente.invalidateQueries({ queryKey: ['entradas'] });
    },
  });
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <BotaoPasso icone={RefreshCw} onClick={() => conferir.mutate()} desabilitado={conferir.isPending}>
        {conferir.isPending ? 'Conferindo…' : 'Conferir de novo'}
      </BotaoPasso>
      {conferir.error ? (
        <span className="text-2xs text-status-divergencia">{conferir.error instanceof ErroHttp ? conferir.error.message : String(conferir.error)}</span>
      ) : null}
    </span>
  );
}

const DICA_SLACK = 'Também pelo Slack, na thread da nota ou do aviso da OC: aceitar <motivo> (quem aprova DOH) ou recusar <motivo>.';

const REGRAS_OC = ['oc', 'fornecedor', 'destinatario', 'oc_fechada', 'item_fora_oc'];
const CONFERENCIA: Status[] = ['capturada', 'aguardando_xml', 'sem_oc', 'divergente', 'recusada'];
const DEPOIS_DA_ENTRADA: Status[] = [
  'aguardando_agendamento',
  'agendada',
  'aguardando_transferencia',
  'em_transito_full',
  'em_conferencia',
  'divergencia_recebimento',
  'estocada',
  'disponivel_venda',
];
const AGENDADA: Status[] = ['agendada', 'em_transito_full', 'em_conferencia', 'divergencia_recebimento', 'estocada', 'disponivel_venda'];
const RECEBIDA: Status[] = ['estocada', 'disponivel_venda'];

const diaHora = (iso: string): string =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

/** Quando a nota entrou em cada etapa, pela linha do tempo (a última vez). */
const quandoEntrou = (d: DetalheNota, status: Status[]): string | null =>
  [...d.linhaDoTempo].reverse().find((e) => e.tipo === 'status' && e.para != null && status.includes(e.para))?.quando ?? null;

/**
 * O agendamento: origem → destino, data e janela; se a nota passou da etapa sem
 * agendamento no WMS (a cega saiu no ERP), diz isso em vez de "agendar".
 */
function textoAgendamento(d: DetalheNota): string {
  const n = d.nota;
  const origem = (caixaAlta(n.emitenteNome) ?? 'fornecedor').split(' ').slice(0, 2).join(' ');
  const destino = n.destinoNome ?? (n.empresa != null ? `empresa ${n.empresa}` : 'destino');
  if (n.agendaData) {
    return `${origem} → ${destino} · ${n.agendaData.split('-').reverse().join('/')}${n.agendaJanela ? `, ${n.agendaJanela.replace('-', ' às ')}` : ''}`;
  }
  if (n.status === 'aguardando_transferencia') return `${origem} → ${destino} · falta a NF de transferência.`;
  if (AGENDADA.includes(n.status)) {
    // Passou daqui sem agendamento no WMS: a cega (ou a chegada) veio direto do ERP.
    const chegou = quandoEntrou(d, ['em_conferencia', 'em_transito_full']);
    return `Sem agendamento no WMS: ${n.recebimento ? `a cega ${n.recebimento} saiu direto no ERP` : 'seguiu direto pelo ERP'}${chegou ? ` (${diaHora(chegou)})` : ''}.`;
  }
  return `${origem} → ${destino} · agendar o recebimento.`;
}

/** O recebimento: em trânsito, quando chegou (e onde a cega está), ou quando foi estocada. */
function textoRecebimento(d: DetalheNota): string {
  const n = d.nota;
  const destino = n.destinoNome ?? (n.empresa != null ? `empresa ${n.empresa}` : 'o destino');
  if (RECEBIDA.includes(n.status)) {
    const em = quandoEntrou(d, ['estocada', 'disponivel_venda']);
    return `Estocada${em ? ` em ${diaHora(em)}` : ''}${n.chegouDivergente ? ', com a contagem diferente da nota' : ''}.`;
  }
  if (n.status === 'em_transito_full') {
    const desde = quandoEntrou(d, ['em_transito_full']);
    return `Em trânsito para ${destino}${desde ? ` desde ${diaHora(desde)}` : ''}.`;
  }
  if (n.status === 'em_conferencia' || n.status === 'divergencia_recebimento') {
    const chegou = quandoEntrou(d, ['em_conferencia']);
    return (
      `Chegou${chegou ? ` em ${diaHora(chegou)}` : ''}${n.recebimento ? ` · cega ${n.recebimento}` : ''}` +
      (n.recebimentoProgresso ? ` · ${textoProgresso(n.recebimentoProgresso)}` : ' · contar, conferir e guardar') +
      (n.status === 'divergencia_recebimento' ? ' · contagem diferente da nota' : '') +
      '.'
    );
  }
  if (n.status === 'agendada' && n.agendaData) return `Previsto para ${n.agendaData.split('-').reverse().join('/')}${n.agendaJanela ? `, ${n.agendaJanela.replace('-', ' às ')}` : ''}.`;
  return 'Quando chegar.';
}

const resumo = (lista: Divergencia[]): string =>
  lista
    .slice(0, 2)
    .map((x) => x.mensagem.replace(/^Item \d+ · /, '').replace(/\s*\(toler[^)]*\)/, ''))
    .join(' · ') + (lista.length > 2 ? ` · e mais ${lista.length - 2}` : '');

export function Roteiro({
  d,
  deParaAberto,
  aoAgendar,
  irParaAcoes,
}: {
  d: DetalheNota;
  deParaAberto: (modo: 'pendencia' | 'cadastro') => React.ReactNode;
  aoAgendar: () => void;
  irParaAcoes: () => void;
}): JSX.Element | null {
  const n = d.nota;
  // Aberto ao abrir a gaveta; recolhido mostra uma linha com o passo atual.
  const [recolhido, definirRecolhido] = React.useState(false);
  const comprarSlack = d.slackPessoas?.comprador ?? [];
  const irAcoes = (rotulo: string): JSX.Element => (
    <BotaoPasso icone={ArrowDown} onClick={irParaAcoes}>
      {rotulo}
    </BotaoPasso>
  );
  // Qual comparação nota × ERP está aberta: associar (item sem SKU) ou gravar o de-para (achado pelo EAN).
  const [abrirDePara, definirAbrirDePara] = React.useState<'pendencia' | 'cadastro' | null>(null);
  if (['cancelada', 'fora_do_fluxo'].includes(n.status)) return null;
  // "JOAQUIM JUNIOR" → "Joaquim": o selo diz "pendente com Joaquim".
  const nomeComprador = n.compradorNome
    ? n.compradorNome.split(' ')[0]!.charAt(0).toUpperCase() + n.compradorNome.split(' ')[0]!.slice(1).toLowerCase()
    : 'o comprador';
  const comprador = `pendente com ${nomeComprador}`;
  const div = d.divergencias;
  const de = (regras: string[]): Divergencia[] => div.filter((x) => regras.includes(x.regra));
  const emConferencia = CONFERENCIA.includes(n.status);
  const conferida = !emConferencia;

  // ---------------------------------------------------------- conferência com a OC
  const daOc = de(REGRAS_OC);
  // Divergência que fala de OUTRA OC que não a da nota é de uma conferência antiga
  // (ex.: OC trocada depois): não é o estado atual, é conferir de novo.
  const desatualizada = n.pedidoCompra != null && daOc.some((x) => [...x.mensagem.matchAll(/\bOC (\d+)/g)].some((m) => Number(m[1]) !== n.pedidoCompra));
  const passos: Passo[] = [
    {
      titulo: 'OC',
      estado: n.pedidoCompra == null ? 'pendente' : daOc.length ? 'pendente' : 'feito',
      detalhe:
        n.pedidoCompra == null
          ? 'Sem OC: indique qual OC é desta nota.'
          : desatualizada
            ? `A nota está na OC ${n.pedidoCompra}${n.ocOrigem === 'indicada' ? ' (indicada)' : ''}, mas a conferência aberta é de outra OC: confira de novo.`
            : daOc.length
              ? `OC ${n.pedidoCompra}: ${resumo(daOc)}`
              : `OC ${n.pedidoCompra}${n.ocOrigem === 'indicada' ? ' (indicada)' : ''}`,
      dono: n.pedidoCompra == null || daOc.length ? comprador : null,
      // Sem OC já tem o "Perguntar a OC ao comprador" (com a DANFE); com OC errada, é perguntar a certa.
      ...(n.pedidoCompra == null ? {} : { chamar: comprarSlack, chamarRotulo: `Perguntar a OC certa a ${nomeComprador} no Slack`, divs: daOc }),
      ...(desatualizada ? { extra: <ConferirDeNovo chave={n.chave} /> } : n.pedidoCompra == null ? { extra: <PerguntarOc d={d} /> } : {}),
      ...(!desatualizada && (n.pedidoCompra == null || daOc.length) ? { acao: <UsarOc d={d} /> } : {}),
    },
  ];
  const semCadastro = de(['produto']);
  const semSku = d.itens.filter((i) => i.produto == null).length;
  passos.push({
    titulo: 'Produto',
    estado: semCadastro.length || semSku ? 'pendente' : 'feito',
    detalhe: semSku && d.fornecedorNoErp === false
      ? `O fornecedor (CNPJ ${n.emitenteCnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}) não está cadastrado no ERP: o de-para é por fornecedor, então primeiro o cadastro cria a entidade. Depois disso, associe os ${semSku} item(ns) aqui.`
      : semSku
      ? `${semSku} de ${d.itens.length} item(ns) sem SKU no ERP: associe abaixo.`
      : `${d.itens.length} de ${d.itens.length} itens com SKU no ERP.`,
    dono: semCadastro.length || semSku ? 'pendente com o cadastro ou o comprador' : null,
    extra: <ItensDoProduto d={d} aoAbrir={(m) => definirAbrirDePara((x) => (x === m ? null : m))} aberto={abrirDePara} />,
  });
  const preco = de(['preco']);
  passos.push({
    titulo: 'Preço',
    estado: preco.length ? 'pendente' : n.pedidoCompra == null || daOc.length ? 'depois' : 'feito',
    detalhe: preco.length ? resumo(preco) : n.pedidoCompra == null || daOc.length ? 'Confere depois da OC.' : 'Dentro do pedido e do pré-pedido.',
    dono: preco.length ? comprador : null,
    ...(preco.length ? { dica: DICA_SLACK, aprovacao: true, regras: ['preco'], impacto: impacto(preco, d.itens), chamar: comprarSlack, divs: preco } : {}),
  });
  const qtd = de(['quantidade']);
  passos.push({
    titulo: 'Quantidade',
    estado: qtd.length ? 'pendente' : n.pedidoCompra == null || daOc.length ? 'depois' : 'feito',
    detalhe: qtd.length ? resumo(qtd) : n.pedidoCompra == null || daOc.length ? 'Confere depois da OC.' : 'Dentro do saldo do pedido.',
    dono: qtd.length ? comprador : null,
    ...(qtd.length ? { dica: DICA_SLACK, aprovacao: true, regras: ['quantidade'], impacto: impacto(qtd, d.itens), chamar: comprarSlack, divs: qtd } : {}),
  });
  const pre = de(['fora_pre_pedido']);
  if (pre.length) {
    passos.push({ titulo: 'Pré-pedido', estado: 'pendente', detalhe: resumo(pre), dono: comprador, dica: DICA_SLACK, aprovacao: true, regras: ['fora_pre_pedido'], impacto: impacto(pre, d.itens), chamar: comprarSlack, divs: pre });
  }

  // ---------------------------------------------------------- fases da esteira
  const fase = (titulo: string, feito: boolean, agora: boolean, detalhe: string, dono: string, acao?: React.ReactNode): Passo => ({
    titulo,
    estado: feito ? 'feito' : agora ? 'pendente' : 'depois',
    detalhe,
    dono: agora ? dono : null,
    chamar: [],
    ...(acao ? { acao } : {}),
  });
  // Agendamento que não houve no WMS: a nota passou da etapa (a cega saiu no ERP) sem nunca ter sido agendada.
  const agendamentoFora = AGENDADA.includes(n.status) && !n.agendaData && !quandoEntrou(d, ['agendada', 'aguardando_transferencia']);
  passos.push(
    fase(
      'Entrada fiscal',
      DEPOIS_DA_ENTRADA.includes(n.status),
      n.status === 'validada',
      n.nfCompra ? `NF de compra ${n.nfCompra} no ERP.` : n.status === 'validada' ? 'Bateu com a OC: dar entrada no ERP.' : 'Depois da conferência.',
      'pendente com o fiscal',
      irAcoes('Dar entrada no ERP'),
    ),
    // Importada com "ignorar validação": sem estoque, a NF de transferência não sai (NF 314690, 30/10/2026).
    ...(n.compraSemValidacao
      ? [
          {
            titulo: 'Validar a compra no ERP',
            estado: 'pendente' as Estado,
            detalhe: `A NF de compra ${n.nfCompra} foi importada sem validação: ainda sem título a pagar e sem estoque. Validar aqui gera os dois, como salvar no Procfit. Sem isso, a NF de transferência não sai.`,
            dono: 'pendente com o fiscal',
            chamar: [],
            acao: <ValidarCompra chave={n.chave} />,
          },
        ]
      : []),
    fase(
      'Agendamento',
      AGENDADA.includes(n.status),
      n.status === 'aguardando_agendamento' || n.status === 'aguardando_transferencia',
      textoAgendamento(d),
      'pendente com a logística',
      n.status === 'aguardando_agendamento' ? (
        <BotaoPasso icone={CalendarPlus} onClick={aoAgendar}>
          Agendar recebimento
        </BotaoPasso>
      ) : (
        irAcoes('Ver a NF de transferência')
      ),
    ),
    {
      ...fase(
        'Recebimento',
        RECEBIDA.includes(n.status),
        ['agendada', 'em_transito_full', 'em_conferencia', 'divergencia_recebimento'].includes(n.status),
        textoRecebimento(d),
        'pendente com o CD',
        irAcoes(
          n.status === 'agendada'
            ? 'Confirmar a chegada'
            : n.status === 'em_transito_full'
              ? 'Confirmar a chegada no Full'
              : n.status === 'divergencia_recebimento'
                ? 'Decidir a divergência da contagem'
                : 'Ver a cega',
        ),
      ),
      // Chegou divergente: o que a cega contou diferente da nota, à vista no passo (antes só o selo dizia).
      ...(d.contagemCega?.itens.length ? { extra: <DiferencasCega c={d.contagemCega} /> } : {}),
    },
  );
  const agendamento = passos.find((p) => p.titulo === 'Agendamento');
  if (agendamento && agendamentoFora) agendamento.fora = true;

  // Conferida: os passos da conferência viram uma linha só ("Conferência com a OC ✓").
  const visiveis: Passo[] = conferida
    ? [
        {
          titulo: 'Conferência com a OC',
          estado: div.length ? 'pendente' : 'feito',
          detalhe: div.length ? `Entrou com divergência: ${resumo(div)}` : `OC ${n.pedidoCompra ?? '—'}${n.ocOrigem === 'indicada' ? ' (indicada)' : ''}`,
          dono: div.length ? comprador : null,
          // O que a conferência olhou, item a item: "conferida" sozinho parecia etapa pulada.
          extra: <SelosConferencia d={d} />,
          ...(div.length ? { dica: DICA_SLACK, aprovacao: true, impacto: impacto(div, d.itens), chamar: comprarSlack, divs: div } : {}),
        },
        ...passos.slice(-3),
      ]
    : passos;
  const proximo = visiveis.findIndex((p) => p.estado === 'pendente');
  const tudoFeito = visiveis.every((p) => p.estado === 'feito') && !d.contagemCega?.itens.length;
  // Há quanto tempo a nota está na etapa: âmbar a partir de 24 h, vermelho a partir de 48 h.
  const horasParada = (Date.now() - new Date(n.statusDesde).getTime()) / 3_600_000;
  // "Chamar Joaquim no Slack" / "Chamar a logística no Slack": o selo sem o "pendente com".
  const rotuloChamar = (p: Passo): string => p.chamarRotulo ?? `Chamar ${(p.dono ?? '').replace(/^pendente com /, '')} no Slack`;
  const chamarNoSlack = (p: Passo): void => {
    window.dispatchEvent(
      new CustomEvent('wms-conversa-preencher', {
        detail: { chave: n.chave, texto: mensagemDoPasso(d, p), marcar: p.chamar ?? [] },
      }),
    );
  };

  return (
    <section
      className={cn(
        'rounded-xl border border-borda bg-superficie p-4',
        // Recolhido com passo pendente: a faixa âmbar não deixa o card sumir no meio da gaveta.
        recolhido && proximo >= 0 && 'border-l-4 border-l-status-atencao py-3',
      )}
    >
      <div className={cn('flex items-center justify-between gap-2', !recolhido && 'mb-2')}>
        {/* Nada pendente nem por vir: "O que falta" não faz sentido; o título diz que está tudo feito. */}
        {tudoFeito ? (
          <p className="rotulo flex items-center gap-1.5 text-status-livre">
            <CheckCircle2 className="h-3.5 w-3.5" /> Tudo feito: nada falta nesta nota
          </p>
        ) : (
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <p className="rotulo">{proximo >= 0 ? 'O que falta para seguir' : 'Andamento da nota'}</p>
            {/* Recolhido: o passo atual continua à vista, com o mesmo destaque do aberto. */}
            {recolhido && proximo >= 0 ? (
              <>
                <span className="inline-flex items-center gap-1 rounded-full bg-status-atencaoFundo px-2 py-0.5 text-xs font-semibold text-status-atencao">
                  <CircleDot className="h-3 w-3" /> agora: {proximo + 1}. {visiveis[proximo]!.titulo}
                </span>
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-2xs font-semibold',
                    horasParada >= 48
                      ? 'bg-status-divergenciaFundo text-status-divergencia'
                      : horasParada >= 24
                        ? 'bg-status-atencaoFundo text-status-atencao'
                        : 'bg-superficie2 text-textoSuave',
                  )}
                >
                  parado {haQuanto(n.statusDesde)}
                </span>
                {visiveis[proximo]!.dono ? <span className="text-2xs text-textoFraco">{visiveis[proximo]!.dono}</span> : null}
              </>
            ) : null}
          </div>
        )}
        <button
          type="button"
          onClick={() => definirRecolhido((x) => !x)}
          aria-expanded={!recolhido}
          className="inline-flex shrink-0 items-center gap-1 text-2xs font-semibold text-textoSuave hover:text-texto"
        >
          {recolhido ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
          {recolhido ? 'Mostrar' : 'Recolher'}
        </button>
      </div>
      {recolhido ? null : (
      <ol className="space-y-0">
        {visiveis.map((p, i) => {
          const Icone = p.fora ? SkipForward : p.estado === 'feito' ? CheckCircle2 : p.estado === 'pendente' ? CircleDot : Circle;
          const atual = i === proximo;
          return (
            <li key={p.titulo} className="relative flex gap-3 pb-3 last:pb-0">
              {i < visiveis.length - 1 ? <span className="absolute left-[9px] top-5 h-[calc(100%-12px)] w-px bg-borda" aria-hidden /> : null}
              <Icone
                className={cn(
                  'relative mt-0.5 h-[18px] w-[18px] shrink-0 bg-superficie',
                  p.fora ? 'text-textoSuave' : p.estado === 'feito' ? 'text-status-livre' : p.estado === 'pendente' ? 'text-status-atencao' : 'text-bordaForte',
                )}
              />
              <div className={cn('min-w-0 flex-1 text-xs', p.estado === 'depois' && 'opacity-60')}>
                <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <b className={cn('text-[13px]', atual ? 'text-texto' : 'text-textoFraco')}>
                    {i + 1}. {p.titulo}
                  </b>
                  {atual ? (
                    <span className="rounded-full bg-status-atencaoFundo px-1.5 py-0.5 text-2xs font-semibold text-status-atencao">agora</span>
                  ) : null}
                  {atual ? (
                    <span
                      title={`Na etapa desde ${new Date(n.statusDesde).toLocaleString('pt-BR')}`}
                      className={cn(
                        'rounded-full px-1.5 py-0.5 text-2xs font-semibold',
                        horasParada >= 48
                          ? 'bg-status-divergenciaFundo text-status-divergencia'
                          : horasParada >= 24
                            ? 'bg-status-atencaoFundo text-status-atencao'
                            : 'bg-superficie2 text-textoSuave',
                      )}
                    >
                      parado {haQuanto(n.statusDesde)}
                    </span>
                  ) : null}
                  {p.fora ? <span className="rounded-full border border-dashed border-bordaForte px-1.5 py-0.5 text-2xs text-textoSuave">feito fora do WMS</span> : null}
                  {p.impacto != null && atual ? (
                    <span className="rounded-full bg-status-divergenciaFundo px-1.5 py-0.5 text-2xs font-bold text-status-divergencia" title="Diferença em reais nas divergências deste passo">
                      +{brl(p.impacto)} de diferença
                    </span>
                  ) : null}
                  {p.dono ? (
                    <span className="rounded-full border border-borda px-1.5 py-0.5 text-2xs text-textoFraco">{p.dono}</span>
                  ) : null}
                </p>
                <p className="text-textoFraco">{p.detalhe}</p>
                {p.extra ? <div className="mt-1">{p.extra}</div> : null}
                {p.acao && atual ? <div className="mt-1.5">{p.acao}</div> : null}
                {(() => {
                  // Quem é a vez, como ação: abre a conversa da nota com o texto pronto e (quando o WMS sabe) já marcado.
                  const chamar =
                    p.dono && atual && p.chamar ? (
                      <BotaoPasso icone={LogoSlack} onClick={() => chamarNoSlack(p)}>
                        {rotuloChamar(p)}
                      </BotaoPasso>
                    ) : null;
                  // Passo de decisão: Aceitar · Recusar · Pedir autorização · Chamar, numa linha só.
                  if (p.aprovacao && atual) return <DecisaoDoPasso d={d} regras={p.regras} chamar={chamar} />;
                  return chamar ? <div className="mt-1.5">{chamar}</div> : null;
                })()}
                {p.dica && atual ? <p className="mt-1 text-2xs text-textoSuave">{p.dica}</p> : null}
              </div>
            </li>
          );
        })}
      </ol>
      )}
      {abrirDePara && !recolhido ? <div className="mt-3">{deParaAberto(abrirDePara)}</div> : null}
    </section>
  );
}
